import { PostHogProvider, usePostHog } from "posthog-js/react";
import { Fragment, useEffect } from "react";
import { useSmartrrFlags } from "@smartrr/shared/LaunchDarkly";
import { useActiveOrganizationSelector } from "@vendor-app/app/_state/reducers/organizations";
import config from "@vendor-app/config";

interface PostHogControlProps {
  enableCapture?: boolean;
}

/**
 * We only want posthog to capture on specific pages.
 * Use this hook to enable capture on the page.
 * You can use the `enableCapture` parameter to only enable
 * in certain scenarios.
 */
export const usePostHogControl = ({ enableCapture }: PostHogControlProps) => {
  const posthog = usePostHog();
  const { posthogSessionReplay } = useSmartrrFlags();

  useEffect(() => {
    if (!posthog || !posthogSessionReplay || !enableCapture) {
      return;
    }

    posthog.startSessionRecording();

    return () => {
      posthog.stopSessionRecording();
    };
  }, [posthog, enableCapture, posthogSessionReplay]);
};

export const PostHogProviderWrapper = ({ children }) => {
  const posthog = usePostHog();
  const { posthogSessionReplay } = useSmartrrFlags();
  const organizationName = useActiveOrganizationSelector()?.orgName;

  useEffect(() => {
    if (!posthog || !posthogSessionReplay) {
      return;
    }

    posthog.register({
      environment: config.environment,
      ...(organizationName && { "Organization Name": organizationName }),
    });
  }, [posthog, posthogSessionReplay, organizationName]);

  if (!posthogSessionReplay) {
    return <Fragment>{children}</Fragment>;
  }

  return (
    <PostHogProvider
      apiKey={config.posthog.clientId}
      options={{
        api_host: config.posthog.publicPosthogHost,
        autocapture: true,
        capture_pageview: false,
        disable_session_recording: true,
      }}
    >
      {children}
    </PostHogProvider>
  );
};
